import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchMe, updateMe, updateNotificationPrefs, completeOnboarding, recordConsent, syncEmailFromClerk, deleteMe } from './api';
import { uploadAvatarPhoto } from '../../shared/api/cloudinary';
import { useAppStore } from '../../store/useAppStore';

export const USERS_KEY = ['users'] as const;

export function useMe() {
  return useQuery({
    queryKey: [...USERS_KEY, 'me'],
    queryFn: fetchMe,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  const updateUser = useAppStore((s) => s.updateUser);
  return useMutation({
    mutationFn: updateMe,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      updateUser(updated);
    },
  });
}

// Kept separate from useUpdateMe so its own pending/error state doesn't interfere with the
// profile-details form's "Save changes" button — mirrors rteh/src/features/users/hooks.ts.
export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  const updateUser = useAppStore((s) => s.updateUser);
  return useMutation({
    mutationFn: updateNotificationPrefs,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      updateUser(updated);
    },
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  const updateUser = useAppStore((s) => s.updateUser);
  return useMutation({
    mutationFn: async (file: File) => {
      const avatarUrl = await uploadAvatarPhoto(file);
      return updateMe({ avatarUrl });
    },
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      updateUser(updated);
    },
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  const updateUser = useAppStore((s) => s.updateUser);
  return useMutation({
    mutationFn: completeOnboarding,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      updateUser(updated);
    },
  });
}

export function useRecordConsent() {
  const qc = useQueryClient();
  const updateUser = useAppStore((s) => s.updateUser);
  return useMutation({
    mutationFn: recordConsent,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      updateUser(updated);
    },
  });
}

export function useSyncEmailFromClerk() {
  const qc = useQueryClient();
  const updateUser = useAppStore((s) => s.updateUser);
  return useMutation({
    mutationFn: syncEmailFromClerk,
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
      updateUser(updated);
    },
  });
}

export function useDeleteMe() {
  return useMutation({
    mutationFn: deleteMe,
  });
}
